import { useMemo, type Dispatch } from 'react'

import {
  actionLabel,
  CHANNEL_TYPE_LABELS,
  CREATIVE_SLOTS,
  DIGITAL_CHANNEL_TYPES,
  isOwnedChannel,
  OBJECTIVE_DESCRIPTIONS,
  OBJECTIVE_LABELS,
  OFFLINE_CHANNEL_TYPES,
  segmentLabel,
  triggerLabel,
  type CampaignObjective,
  type CampaignWebsite,
  type ChannelDraft,
  type PromoCodeMode,
} from '@/api/campaigns'
import type { FilterOption } from '@/api/types'
import { formatCurrency, type FormatOptions } from '@/design'
import { formatDateKey } from '@/lib/filters'

import { CopyField, formatDateRange, KindBadge, QrImage } from '../components'
import { amountValue, AudienceFields, AutomationFields, Field, IssueList, parseAmount } from '../fields'
import { qrSvg } from '../qr'
import { buildTrackingUrl, promoPrefix, slugify } from '../tracking'
import { issuesFor, WIZARD_STEPS, type DraftIssue, type StepKey, type WizardAction, type WizardState } from './state'

export interface StepProps {
  state: WizardState
  dispatch: Dispatch<WizardAction>
  issues: readonly DraftIssue[]
  /** The message to show against a field right now, if any. */
  fieldError: (field: string) => string | null
  websites: readonly CampaignWebsite[]
  competitions: readonly FilterOption[]
  money: FormatOptions
  /** The objectives the current client can store. */
  objectives: readonly CampaignObjective[]
}

export const STEP_DESCRIPTIONS: Record<StepKey, string> = {
  basics: 'Name the campaign, choose where it sends people and which competitions it promotes.',
  objective: 'What the campaign is for. It decides how results are judged.',
  channels: 'Every channel, digital and offline. Each gets its own tracking.',
  audience: 'Who the campaign targets, and who it leaves out.',
  budget: 'The campaign total and each paid channel’s share. Owned channels need none.',
  creative: 'The asset each channel carries.',
  tracking: 'Generated for every channel: a UTM-tagged link, a short link, a QR code and a promo code. Edit any of them.',
  automation: 'Optional: what should happen automatically while the campaign runs.',
  review: 'Check everything before saving. Anything missing is highlighted.',
}

function Basics({ state, dispatch, fieldError, websites, competitions }: StepProps) {
  const { draft } = state
  const set = (patch: Extract<WizardAction, { type: 'setBasics' }>['patch']) =>
    dispatch({ type: 'setBasics', patch })
  const offered = competitions.filter((option) => option.group !== 'Closed')
  return (
    <div className="camp-form">
      <Field label="Campaign name" error={fieldError('name')} wide>
        {(props) => <input {...props} value={draft.name} maxLength={200} onChange={(event) => set({ name: event.target.value })} />}
      </Field>
      <Field label="Description" wide>
        {(props) => (
          <textarea {...props} rows={3} maxLength={5000} value={draft.description} onChange={(event) => set({ description: event.target.value })} />
        )}
      </Field>
      <Field label="Website" error={fieldError('websiteId')} hint="Tracking links point here.">
        {(props) => (
          <select {...props} value={draft.websiteId} onChange={(event) => set({ websiteId: event.target.value })}>
            <option value="">Choose a website</option>
            {websites.map((website) => (
              <option key={website.id} value={website.id}>
                {website.name}
                {website.environment === 'production' ? '' : ` (${website.environment})`}
              </option>
            ))}
          </select>
        )}
      </Field>
      <div />
      <Field label="Start date">
        {(props) => <input {...props} type="date" value={draft.startsAt} onChange={(event) => set({ startsAt: event.target.value })} />}
      </Field>
      <Field label="End date" error={fieldError('endsAt')}>
        {(props) => (
          <input {...props} type="date" min={draft.startsAt || undefined} value={draft.endsAt} onChange={(event) => set({ endsAt: event.target.value })} />
        )}
      </Field>
      <fieldset className="camp-fieldset camp-form__wide">
        <legend>Linked competitions</legend>
        {offered.length === 0 ? <p className="camp-muted">No open or upcoming competitions.</p> : null}
        <div className="camp-choices">
          {offered.map((option) => (
            <label key={option.value} className="camp-choice">
              <input
                type="checkbox"
                checked={draft.competitionIds.includes(option.value)}
                onChange={(event) =>
                  set({
                    competitionIds: event.target.checked
                      ? [...draft.competitionIds, option.value]
                      : draft.competitionIds.filter((id) => id !== option.value),
                  })
                }
              />
              <span className="camp-choice__text">
                <span>{option.label}</span>
                {option.group ? <span className="camp-hint">{option.group}</span> : null}
              </span>
            </label>
          ))}
        </div>
      </fieldset>
    </div>
  )
}

function Objective({ state, dispatch, fieldError, objectives }: StepProps) {
  const { draft } = state
  return (
    <>
      <fieldset className="camp-fieldset">
        <legend>Objective</legend>
        <div className="camp-choices">
          {objectives.map((objective) => (
            <label key={objective} className="camp-choice">
              <input
                type="radio"
                name="objective"
                checked={draft.objective === objective}
                onChange={() => dispatch({ type: 'setObjective', objective })}
              />
              <span className="camp-choice__text">
                <span>{OBJECTIVE_LABELS[objective]}</span>
                <span className="camp-hint">{OBJECTIVE_DESCRIPTIONS[objective]}</span>
              </span>
            </label>
          ))}
        </div>
        {fieldError('objective') ? <span className="camp-error">{fieldError('objective')}</span> : null}
      </fieldset>
      {draft.objective === 'custom' ? (
        <div className="camp-form">
          <Field label="Custom objective" error={fieldError('customObjective')} wide>
            {(props) => (
              <input
                {...props}
                value={draft.customObjective}
                maxLength={200}
                onChange={(event) => dispatch({ type: 'setCustomObjective', text: event.target.value })}
              />
            )}
          </Field>
        </div>
      ) : null}
    </>
  )
}

function Channels({ state, dispatch, fieldError }: StepProps) {
  const { draft } = state
  const chosen = (type: string) => draft.channels.some((channel) => channel.key === type)
  const custom = draft.channels.filter((channel) => channel.channelType === 'custom')
  const group = (legend: string, types: readonly Exclude<(typeof DIGITAL_CHANNEL_TYPES)[number], never>[]) => (
    <fieldset className="camp-fieldset">
      <legend>{legend}</legend>
      <div className="camp-choices">
        {types.map((type) => (
          <label key={type} className="camp-choice">
            <input type="checkbox" checked={chosen(type)} onChange={() => dispatch({ type: 'toggleChannel', channelType: type })} />
            <span>{CHANNEL_TYPE_LABELS[type]}</span>
          </label>
        ))}
      </div>
    </fieldset>
  )
  return (
    <>
      {group('Digital', DIGITAL_CHANNEL_TYPES)}
      {group('Offline', OFFLINE_CHANNEL_TYPES)}
      <fieldset className="camp-fieldset">
        <legend>Custom</legend>
        <div className="camp-panels">
          {custom.map((channel, index) => (
            <div key={channel.key} className="camp-form camp-panel">
              <Field label={`Custom channel ${index + 1} name`} error={fieldError(`${channel.key}.name`)}>
                {(props) => (
                  <input {...props} value={channel.name} maxLength={200} onChange={(event) => dispatch({ type: 'renameChannel', key: channel.key, name: event.target.value })} />
                )}
              </Field>
              <Field label="Kind">
                {(props) => (
                  <select
                    {...props}
                    value={channel.kind}
                    onChange={(event) => dispatch({ type: 'setChannelKind', key: channel.key, kind: event.target.value === 'digital' ? 'digital' : 'offline' })}
                  >
                    <option value="offline">Offline</option>
                    <option value="digital">Digital</option>
                  </select>
                )}
              </Field>
              <div className="camp-form__actions camp-form__wide">
                <button type="button" className="ci-button" data-variant="ghost" onClick={() => dispatch({ type: 'removeChannel', key: channel.key })}>
                  Remove {channel.name || `custom channel ${index + 1}`}
                </button>
              </div>
            </div>
          ))}
        </div>
        <div>
          <button type="button" className="ci-button" onClick={() => dispatch({ type: 'addCustomChannel' })}>
            Add custom channel
          </button>
        </div>
      </fieldset>
      <p className="camp-muted" role="status">
        {draft.channels.length === 0
          ? 'No channels chosen yet.'
          : `${draft.channels.length} chosen: ${draft.channels.filter((channel) => channel.kind === 'digital').length} digital, ${draft.channels.filter((channel) => channel.kind === 'offline').length} offline.`}
      </p>
      {fieldError('channels') ? <p className="camp-error">{fieldError('channels')}</p> : null}
    </>
  )
}

function Audience({ state, dispatch, fieldError }: StepProps) {
  return (
    <AudienceFields
      audience={state.draft.audience}
      onChange={(patch) => dispatch({ type: 'setAudience', patch })}
      error={fieldError('exclude')}
      geographyError={fieldError('geography')}
    />
  )
}

function Budget({ state, dispatch, fieldError, money }: StepProps) {
  const { draft } = state
  const planned = draft.channels.reduce((sum, channel) => sum + (isOwnedChannel(channel.channelType) ? 0 : (channel.budget ?? 0)), 0)
  return (
    <div className="camp-form">
      <Field label={`Campaign total (${money.currency ?? 'GBP'})`} error={fieldError('totalBudget')} hint="Leave blank for no overall cap.">
        {(props) => (
          <input
            {...props}
            type="number"
            min="0"
            step="0.01"
            inputMode="decimal"
            value={amountValue(draft.totalBudget)}
            onChange={(event) => dispatch({ type: 'setTotalBudget', budget: parseAmount(event.target.value) })}
          />
        )}
      </Field>
      <dl className="camp-mix">
        <div>
          <dt>Planned across channels</dt>
          <dd>{formatCurrency(planned, money)}</dd>
        </div>
        {draft.totalBudget !== null ? (
          <div>
            <dt>Unallocated</dt>
            <dd>{formatCurrency(draft.totalBudget - planned, money)}</dd>
          </div>
        ) : null}
      </dl>
      <div className="camp-panels camp-form__wide">
        {draft.channels.length === 0 ? <p className="camp-muted">Choose channels first.</p> : null}
        {draft.channels.map((channel) => (
          <div key={channel.key} className="camp-panel">
            <div className="camp-panel__header">
              <span className="camp-inline">
                <span className="camp-panel__title">{channel.name || 'Custom channel'}</span>
                <KindBadge kind={channel.kind} />
              </span>
            </div>
            {isOwnedChannel(channel.channelType) ? (
              <p className="camp-muted">Owned channel — no media budget.</p>
            ) : (
              <Field label={`${channel.name || 'Custom channel'} budget`} error={fieldError(`${channel.key}.budget`)}>
                {(props) => (
                  <input
                    {...props}
                    type="number"
                    min="0"
                    step="0.01"
                    inputMode="decimal"
                    value={amountValue(channel.budget)}
                    onChange={(event) => dispatch({ type: 'setChannelBudget', key: channel.key, budget: parseAmount(event.target.value) })}
                  />
                )}
              </Field>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

function Creative({ state, dispatch, fieldError }: StepProps) {
  return (
    <div className="camp-panels">
      {state.draft.channels.length === 0 ? <p className="camp-muted">Choose channels first.</p> : null}
      {state.draft.channels.map((channel) => {
        const slot = CREATIVE_SLOTS[channel.channelType]
        const name = channel.name || 'Custom channel'
        return (
          <section key={channel.key} className="camp-panel" aria-label={`${name} creative`}>
            <div className="camp-panel__header">
              <h3 className="camp-panel__title">
                {name} · {slot.label}
              </h3>
              <KindBadge kind={channel.kind} />
            </div>
            <p className="camp-hint">{slot.hint}</p>
            <div className="camp-form">
              {slot.fields.headline ? (
                <Field label={`${name} ${slot.fields.headline.toLowerCase()}`}>
                  {(props) => (
                    <input {...props} maxLength={300} value={channel.creative.headline} onChange={(event) => dispatch({ type: 'setCreative', key: channel.key, patch: { headline: event.target.value } })} />
                  )}
                </Field>
              ) : null}
              {slot.fields.assetUrl ? (
                <Field label={`${name} ${slot.fields.assetUrl.toLowerCase()}`} error={fieldError(`${channel.key}.assetUrl`)}>
                  {(props) => (
                    <input
                      {...props}
                      type="url"
                      placeholder="https://"
                      value={channel.creative.assetUrl}
                      onChange={(event) => dispatch({ type: 'setCreative', key: channel.key, patch: { assetUrl: event.target.value } })}
                    />
                  )}
                </Field>
              ) : null}
              {slot.fields.body ? (
                <Field label={`${name} ${slot.fields.body.toLowerCase()}`} wide>
                  {(props) => (
                    <textarea {...props} rows={3} value={channel.creative.body} onChange={(event) => dispatch({ type: 'setCreative', key: channel.key, patch: { body: event.target.value } })} />
                  )}
                </Field>
              ) : null}
            </div>
          </section>
        )
      })}
    </div>
  )
}

const PROMO_MODES: Array<{ value: PromoCodeMode; label: string }> = [
  { value: 'none', label: 'No promo code' },
  { value: 'generate', label: 'Generate a code' },
  { value: 'existing', label: 'Use a code already in the shop' },
]

function TrackingPanel({
  channel,
  campaignSlug,
  website,
  dispatch,
  fieldError,
}: {
  channel: ChannelDraft
  campaignSlug: string
  website: CampaignWebsite | undefined
  dispatch: Dispatch<WizardAction>
  fieldError: (field: string) => string | null
}) {
  const { tracking } = channel
  const name = channel.name || 'Custom channel'
  const set = (patch: Partial<ChannelDraft['tracking']>) => dispatch({ type: 'setTracking', key: channel.key, patch })
  const url = website
    ? buildTrackingUrl(website.origin, tracking.landingPath || null, {
        utmSource: tracking.utmSource || 'source',
        utmMedium: tracking.utmMedium || 'medium',
        utmCampaign: campaignSlug,
        utmContent: tracking.utmContent || null,
        trackingId: null,
      })
    : null
  const preview = useMemo(() => (tracking.qrCode && url ? qrSvg(url) : null), [tracking.qrCode, url])

  return (
    <section className="camp-panel" aria-label={`${name} tracking`}>
      <div className="camp-panel__header">
        <span className="camp-inline">
          <h3 className="camp-panel__title">{name}</h3>
          <KindBadge kind={channel.kind} />
        </span>
        <button type="button" className="ci-button" data-variant="ghost" onClick={() => dispatch({ type: 'resetTracking', key: channel.key })}>
          Reset {name} tracking
        </button>
      </div>
      <div className="camp-tracking">
        <div className="camp-form">
          <Field label={`${name} landing path`} error={fieldError(`${channel.key}.landingPath`)} hint="Blank opens the home page." wide>
            {(props) => <input {...props} placeholder="/competitions/…" value={tracking.landingPath} onChange={(event) => set({ landingPath: event.target.value })} />}
          </Field>
          <Field label={`${name} utm_source`} error={fieldError(`${channel.key}.utmSource`)}>
            {(props) => <input {...props} value={tracking.utmSource} onChange={(event) => set({ utmSource: event.target.value })} />}
          </Field>
          <Field label={`${name} utm_medium`} error={fieldError(`${channel.key}.utmMedium`)}>
            {(props) => <input {...props} value={tracking.utmMedium} onChange={(event) => set({ utmMedium: event.target.value })} />}
          </Field>
          <Field label={`${name} utm_content`} error={fieldError(`${channel.key}.utmContent`)}>
            {(props) => <input {...props} value={tracking.utmContent} onChange={(event) => set({ utmContent: event.target.value })} />}
          </Field>
          <Field label={`${name} promo code`}>
            {(props) => (
              <select {...props} value={tracking.promoMode} onChange={(event) => set({ promoMode: event.target.value as PromoCodeMode })}>
                {PROMO_MODES.map((mode) => (
                  <option key={mode.value} value={mode.value}>
                    {mode.label}
                  </option>
                ))}
              </select>
            )}
          </Field>
          {tracking.promoMode === 'existing' ? (
            <Field label={`${name} shop coupon code`} error={fieldError(`${channel.key}.promoCode`)} hint="Create the coupon in your shop first.">
              {(props) => <input {...props} value={tracking.promoCode} onChange={(event) => set({ promoCode: event.target.value.toUpperCase() })} />}
            </Field>
          ) : null}
          <div className="camp-inline camp-form__wide">
            <label className="camp-choice">
              <input type="checkbox" checked={tracking.shortLink} onChange={(event) => set({ shortLink: event.target.checked })} />
              <span>{name} short link</span>
            </label>
            <label className="camp-choice">
              <input type="checkbox" checked={tracking.qrCode} onChange={(event) => set({ qrCode: event.target.checked })} />
              <span>{name} QR code</span>
            </label>
          </div>
          {url ? (
            <div className="camp-form__wide">
              <CopyField label={`${name} tracking URL`} value={url} />
              <p className="camp-hint">A tracking id (utm_id) is added when you save.</p>
            </div>
          ) : null}
          <dl className="camp-summary camp-form__wide">
            <dt>Short link</dt>
            <dd>{tracking.shortLink ? 'Allocated when you save' : 'None'}</dd>
            <dt>Promo code</dt>
            <dd className="camp-code">
              {tracking.promoMode === 'generate'
                ? `${promoPrefix(campaignSlug)}-···· (allocated when you save)`
                : tracking.promoMode === 'existing'
                  ? tracking.promoCode || '—'
                  : 'None'}
            </dd>
          </dl>
        </div>
        {preview ? (
          <figure className="camp-tracking__qr">
            <QrImage svg={preview} label={`${name} QR code preview`} />
            <figcaption>Preview. After saving, the QR code opens this channel’s own short link; download it from the campaign page.</figcaption>
          </figure>
        ) : null}
      </div>
    </section>
  )
}

function Tracking({ state, dispatch, fieldError, websites }: StepProps) {
  const { draft } = state
  const website = websites.find((candidate) => candidate.id === draft.websiteId)
  const slug = slugify(draft.name)
  return (
    <div className="camp-panels">
      {draft.channels.length === 0 ? <p className="camp-muted">Choose channels first.</p> : null}
      {!website && draft.channels.length > 0 ? <p className="camp-error">Choose a website in Basics to preview tracking links.</p> : null}
      {draft.channels.map((channel) => (
        <TrackingPanel key={channel.key} channel={channel} campaignSlug={slug} website={website} dispatch={dispatch} fieldError={fieldError} />
      ))}
    </div>
  )
}

function Automation({ state, dispatch }: StepProps) {
  return (
    <AutomationFields
      rules={state.draft.automations}
      onChange={(rules) => {
        // Express the new list as the reducer's actions, so the state machine stays the one writer.
        const before = state.draft.automations
        for (const rule of before) if (!rules.some((next) => next.trigger === rule.trigger)) dispatch({ type: 'toggleAutomation', trigger: rule.trigger })
        for (const rule of rules) {
          const previous = before.find((candidate) => candidate.trigger === rule.trigger)
          if (!previous) dispatch({ type: 'toggleAutomation', trigger: rule.trigger })
          if (previous?.action !== rule.action) dispatch({ type: 'setAutomationAction', trigger: rule.trigger, action: rule.action })
        }
      }}
    />
  )
}

function Review({ state, dispatch, issues, websites, competitions, money }: StepProps) {
  const { draft } = state
  const website = websites.find((candidate) => candidate.id === draft.websiteId)
  const blocking = issues.filter((issue) => issue.severity !== 'warning')
  const warnings = issues.filter((issue) => issue.severity === 'warning')
  const missing = (step: StepKey) => issuesFor(issues, step).length > 0
  const edit = (step: StepKey) => (
    <button
      type="button"
      className="ci-button"
      data-variant="ghost"
      onClick={() => dispatch({ type: 'goTo', step: WIZARD_STEPS.findIndex((candidate) => candidate.key === step) })}
    >
      Edit {WIZARD_STEPS.find((candidate) => candidate.key === step)?.label.toLowerCase()}
    </button>
  )
  const competitionNames = draft.competitionIds.map((id) => competitions.find((option) => option.value === id)?.label ?? id)
  const planned = draft.channels.reduce((sum, channel) => sum + (channel.budget ?? 0), 0)
  const withCreative = draft.channels.filter((channel) => channel.creative.headline || channel.creative.body || channel.creative.assetUrl).length

  return (
    <div className="camp-panels">
      {blocking.length > 0 ? (
        <div className="camp-panel" role="alert">
          <p>
            <strong>{blocking.length === 1 ? '1 thing' : `${blocking.length} things`} to fix before launching.</strong>
          </p>
          <IssueList issues={blocking} label="Must fix" />
        </div>
      ) : (
        <p className="camp-muted" role="status">
          Ready to launch.
        </p>
      )}
      {warnings.length > 0 ? (
        <div className="camp-panel">
          <p>Worth checking:</p>
          <IssueList issues={warnings} label="Worth checking" />
        </div>
      ) : null}

      <dl className="camp-summary" aria-label="Campaign summary">
        <dt>Name</dt>
        <dd data-missing={missing('basics') || undefined}>
          {draft.name || '—'} {edit('basics')}
        </dd>
        <dt>Website</dt>
        <dd>{website?.name ?? '—'}</dd>
        <dt>Dates</dt>
        <dd>{formatDateRange(draft.startsAt || null, draft.endsAt || null, (key) => formatDateKey(key, true))}</dd>
        <dt>Competitions</dt>
        <dd>{competitionNames.length ? competitionNames.join(', ') : 'None linked'}</dd>
        <dt>Objective</dt>
        <dd data-missing={missing('objective') || undefined}>
          {draft.objective ? (draft.objective === 'custom' ? draft.customObjective || 'Custom' : OBJECTIVE_LABELS[draft.objective]) : 'Not chosen'}{' '}
          {edit('objective')}
        </dd>
        <dt>Channels</dt>
        <dd data-missing={missing('channels') || undefined}>
          {draft.channels.length
            ? draft.channels.map((channel) => `${channel.name || 'Custom'} (${channel.kind})`).join(', ')
            : 'None chosen'}{' '}
          {edit('channels')}
        </dd>
        <dt>Audience</dt>
        <dd data-missing={missing('audience') || undefined}>
          {draft.audience.include.length ? `Include ${draft.audience.include.map(segmentLabel).join(', ')}` : 'Everyone'}
          {draft.audience.exclude.length ? `; exclude ${draft.audience.exclude.map(segmentLabel).join(', ')}` : ''}
          {draft.audience.geography.length ? `; in ${draft.audience.geography.join(', ')}` : ''} {edit('audience')}
        </dd>
        <dt>Budget</dt>
        <dd data-missing={missing('budget') || undefined}>
          {draft.totalBudget === null ? 'No total' : formatCurrency(draft.totalBudget, money)} · {formatCurrency(planned, money)} planned across channels{' '}
          {edit('budget')}
        </dd>
        <dt>Creative</dt>
        <dd data-missing={missing('creative') || undefined}>
          {withCreative} of {draft.channels.length} channels have creative {edit('creative')}
        </dd>
        <dt>Tracking</dt>
        <dd data-missing={missing('tracking') || undefined}>
          {draft.channels.length
            ? draft.channels
                .map((channel) => {
                  const parts = [
                    channel.tracking.shortLink ? 'short link' : null,
                    channel.tracking.qrCode ? 'QR code' : null,
                    channel.tracking.promoMode !== 'none' ? 'promo code' : null,
                  ].filter(Boolean)
                  return `${channel.name || 'Custom'}: UTM link${parts.length ? `, ${parts.join(', ')}` : ''}`
                })
                .join('; ')
            : '—'}{' '}
          {edit('tracking')}
        </dd>
        <dt>Automation</dt>
        <dd>
          {draft.automations.length
            ? draft.automations.map((rule) => `${triggerLabel(rule.trigger)} → ${actionLabel(rule.action)}`).join('; ')
            : 'None'}{' '}
          {edit('automation')}
        </dd>
      </dl>
    </div>
  )
}

export const STEP_BODIES: Record<StepKey, (props: StepProps) => React.JSX.Element> = {
  basics: Basics,
  objective: Objective,
  channels: Channels,
  audience: Audience,
  budget: Budget,
  creative: Creative,
  tracking: Tracking,
  automation: Automation,
  review: Review,
}
